import { useAccessRestrictedStore } from "@/stores/access-restricted-store";

/**
 * Extract the error message from an API response body.
 * The catalogue service always includes a `message` field on errors.
 * `message` can be a string or string[] (NestJS validation errors).
 * Never substitute an app-level string — surface the API message as-is.
 */
export function apiMessage(body: Record<string, unknown>, status: number): string {
  const msg = body?.message;
  if (Array.isArray(msg)) return msg.join(", ");
  if (typeof msg === "string" && msg) return msg;
  return `HTTP ${status}`;
}

/**
 * Thrown when the API rejects a save because the merged step would be
 * incomplete (400 STEP_INCOMPLETE) — e.g. a room still has no amenity
 * category, or fewer than 2 hero photos remain. Distinct from a plain Error
 * so callers can tell this apart from a genuine local/server data mismatch
 * (a stale id, a deleted room): the draft itself is still valid here, it
 * just isn't complete enough yet, so it should never trigger a
 * discard-local-edits recovery the way a real divergence would.
 */
export class StepIncompleteError extends Error {
  missing: Array<{ field: string; reason: string; message: string }>;
  constructor(message: string, missing: Array<{ field: string; reason: string; message: string }>) {
    super(message);
    this.name = "StepIncompleteError";
    this.missing = missing;
  }
}

/**
 * Thrown for a 403 — the request was well-formed but the role can't do this.
 * Distinct from a plain Error so a catch block can skip its usual toast: the
 * app-wide Access Restricted modal (shown here, as soon as the response is
 * parsed) already tells the user what happened.
 */
export class AccessRestrictedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AccessRestrictedError";
  }
}

/** Shared tail of throwIfNotOk/throwValidationAware once 403 and validation-array cases are ruled out. */
export function throwFromBody(body: Record<string, unknown>, status: number): never {
  const message = apiMessage(body, status);
  if (body?.code === "STEP_INCOMPLETE") {
    const missing = body.missing;
    throw new StepIncompleteError(message, Array.isArray(missing) ? missing as StepIncompleteError["missing"] : []);
  }
  throw new Error(message);
}

export async function throwIfNotOk(
  response: Response,
  options?: { silent403?: boolean },
): Promise<void> {
  if (response.ok || response.status === 204) return;
  const body = await response.json().catch(() => ({}));
  if (response.status === 403) {
    const message = apiMessage(body, response.status);
    if (!options?.silent403) {
      useAccessRestrictedStore.getState().show(message);
    }
    throw new AccessRestrictedError(message);
  }
  throwFromBody(body, response.status);
}
